import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePublicPageMeta } from "../hooks/usePageMeta";

export default function TermsOfService() {
  usePublicPageMeta("/terms");
  return (
    <div className="min-h-screen bg-white" data-testid="terms-of-service-page">
      <Header />
      
      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <h1 
            className="text-3xl lg:text-4xl mb-4"
            style={{ color: '#0f1d2f' }}
          >
            Terms of Service
          </h1>
          <p className="text-sm mb-12" style={{ color: '#0f1d2f', opacity: 0.5 }}>
            Last updated: October 18, 2026
          </p>

          <div className="space-y-10" style={{ color: '#0f1d2f' }}>
            <section>
              <h2 className="text-xl mb-4">1. Acceptance of Terms</h2>
              <p className="text-base leading-relaxed opacity-75">
                By accessing or using American Farmstand's website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">2. Description of Service</h2>
              <p className="text-base leading-relaxed opacity-75">
                American Farmstand is a platform that connects independent American farms with customers seeking quality farm products. We provide a membership-based marketplace where farms can list their products and customers can discover, browse, and purchase directly from farms.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">3. Membership</h2>
              <p className="text-base leading-relaxed opacity-75 mb-4">
                Access to our marketplace requires a paid membership subscription.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-base opacity-75">
                <li>Membership fee is $12 per month</li>
                <li>Subscriptions renew automatically unless cancelled</li>
                <li>You may cancel your membership at any time</li>
                <li>No refunds are provided for partial months</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl mb-4">4. User Accounts</h2>
              <p className="text-base leading-relaxed opacity-75 mb-4">
                When you create an account with us, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-base opacity-75">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl mb-4">5. Purchases and Payments</h2>
              <p className="text-base leading-relaxed opacity-75 mb-4">
                All purchases made through American Farmstand are subject to the following:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-base opacity-75">
                <li>Prices are set by individual farms and may change without notice</li>
                <li>Payment is processed securely through Stripe</li>
                <li>Farm pricing is 0% for each farm's first year (from its first qualifying completed sale), then a 2% Farmstand fee</li>
                <li>Orders are fulfilled directly by the farms</li>
                <li>Shipping times and costs vary by farm and location</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl mb-4">6. Refunds and Returns</h2>
              <p className="text-base leading-relaxed opacity-75">
                Refund and return policies are determined by individual farms. If you have issues with an order, please contact the farm directly or reach out to our customer support team for assistance. We will work with farms to resolve disputes fairly.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">7. For Farms</h2>
              <p className="text-base leading-relaxed opacity-75 mb-4">
                Farms participating on American Farmstand agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-base opacity-75">
                <li>Provide accurate product descriptions and images</li>
                <li>Maintain current pricing and availability</li>
                <li>Fulfill orders in a timely manner</li>
                <li>Comply with all applicable food safety regulations</li>
                <li>Accept our farm pricing: no Farmstand fee for the first year beginning with your first qualifying completed sale, then the standard 2% Farmstand fee, applied to product sales after discounts and excluding tax and shipping. Stripe payment-processing fees apply throughout, including the first year</li>
                <li>Set up Stripe Connect for payment processing</li>
              </ul>
              <p className="text-base leading-relaxed opacity-75 mt-4" data-testid="terms-farm-pricing">
                <strong>Farm pricing.</strong> American Farmstand does not charge farms a registration fee or monthly subscription. For each participating farm, the American Farmstand transaction fee is waived for the first twelve months beginning with the farm's first qualifying completed sale. After this introductory period, a standard 2% American Farmstand transaction fee applies to qualifying product sales after discounts, excluding applicable taxes and shipping. Stripe payment-processing fees apply separately throughout the introductory period and thereafter.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">8. Intellectual Property</h2>
              <p className="text-base leading-relaxed opacity-75">
                The American Farmstand name, logo, website design, and all content are protected by intellectual property laws. You may not use, reproduce, or distribute any content without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">9. Prohibited Activities</h2>
              <p className="text-base leading-relaxed opacity-75 mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-base opacity-75">
                <li>Use the service for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the service</li>
                <li>Upload malicious code or content</li>
                <li>Impersonate any person or entity</li>
                <li>Harvest user information without consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl mb-4">10. Disclaimer of Warranties</h2>
              <p className="text-base leading-relaxed opacity-75">
                American Farmstand is provided "as is" without warranties of any kind. We do not guarantee the quality, safety, or legality of products sold by farms on our platform. Farms are independent businesses responsible for their own products and practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">11. Limitation of Liability</h2>
              <p className="text-base leading-relaxed opacity-75">
                To the fullest extent permitted by law, American Farmstand shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our service. Our total liability shall not exceed the amount you paid for our service in the twelve months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">12. Indemnification</h2>
              <p className="text-base leading-relaxed opacity-75">
                You agree to indemnify and hold harmless American Farmstand and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising from your use of our service or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">13. Governing Law</h2>
              <p className="text-base leading-relaxed opacity-75">
                These Terms of Service shall be governed by and construed in accordance with the laws of the United States. Any disputes shall be resolved in the federal or state courts located in the United States.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">14. Changes to Terms</h2>
              <p className="text-base leading-relaxed opacity-75">
                We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through our website. Continued use of the service after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl mb-4">15. Contact Information</h2>
              <p className="text-base leading-relaxed opacity-75">
                For questions about these Terms of Service, please contact us at:
              </p>
              <p className="text-base opacity-75 mt-4">
                <strong>Email:</strong> hello@american-farmstand.com<br />
                <strong>Address:</strong> American Farmstand, United States
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
